/** Responsibility: mount the app. No logic. */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppTheme } from '@kit/theme';
import App from './App';
import { AuthProvider } from './auth/AuthProvider';
import { SignInGate } from './auth/SignInGate';
import { controlCustomizations } from './theme/controls';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      /**
       * **Kept for a day, not for five minutes.**
       *
       * The default drops a query's cache five minutes after the last
       * component using it unmounts, so leaving the collection page and
       * coming back later was a cold load every time - a blank grid for
       * several seconds on the one page that should always be there. Holding
       * the cache means the cards are on screen immediately and the refetch
       * happens behind them.
       *
       * Costs memory and nothing else: this is a few hundred rows, and a
       * stale read is corrected by the refetch that starts on mount.
       */
      gcTime: 1000 * 60 * 60 * 24,
      // The hub is on the other side of a tunnel; a failed request is far more
      // likely to be a real outage than a blip worth hammering.
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppTheme themeComponents={controlCustomizations}>
          {/* Inside the query client, because a change of user clears its
              cache; outside the router, because the gate replaces the whole
              app rather than one route. */}
          <AuthProvider>
            <SignInGate>
              <App />
            </SignInGate>
          </AuthProvider>
        </AppTheme>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
